/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { ReactNode } from 'react'

import { PageTitle, useTitleInset } from './AppTitleBar'

interface PageHeaderProps {
  title: string
  subtitle?: string
  icon?: ReactNode
  menu?: ReactNode
  actions?: ReactNode
  filters?: ReactNode
  className?: string
}

/**
 * Reusable page header component with title, menu, actions, and filters
 */
const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, icon, menu, actions, filters, className = '' }) => {
  const inset = useTitleInset()
  // Kept to the height of a small button plus a little padding: the subtitle sits beside the
  // title rather than under it.
  return (
    <div
      className={`py-1 pr-4 ${inset} min-h-10 rounded-box shadow-md mb-4 relative z-20 bg-primary text-primary-content ${className}`}
    >
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 min-h-8">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 min-w-0">
            {icon && <div className="text-primary text-lg">{icon}</div>}
            <div className="flex items-baseline gap-3 min-w-0">
              <PageTitle className="truncate">{title}</PageTitle>
              {/* The container is bg-primary, so the subtitle has to use primary-content.
                  base-content is the *page* foreground and renders dark-on-dark here. */}
              {subtitle && <p className="text-sm text-primary-content/70 truncate">{subtitle}</p>}
            </div>
          </div>
          {menu && <div className="flex gap-2">{menu}</div>}
        </div>

        <div className="flex items-center gap-4">
          {filters && <div className="flex flex-wrap gap-2">{filters}</div>}
          {actions && <div>{actions}</div>}
        </div>
      </div>
    </div>
  )
}

export default PageHeader
